/**
 * The two halves of searching documents that are NOT the query itself.
 *
 * WHAT IS SEARCHED IS THE BODY, and that is the rule rather than an omission: /t11/v1/document_search
 * ranks the prose of documents this persona is entitled to see, and nothing else. A title is not
 * body text, so it is not in that index and must never be added to it -- the index knows nothing
 * about personas, and widening what it returns widens what a filter downstream has to take back.
 *
 * So a document is found by its NAME the only way that keeps the rule: from the list this page has
 * already been given. That list is QMS's answer to "which documents may this persona see", under
 * the persona in the headers -- the same answer the search's own filter is built from. Matching a
 * name against rows already on screen cannot reveal a document the reader was not already shown.
 */
import type { DocumentRow } from "../components/Docs/DocumentCard";

/** One run of snippet text, and whether the server marked it as the match. */
export type Part = { text: string; hit: boolean };

/**
 * `ts_headline`'s output, split into runs.
 *
 * NOT `dangerouslySetInnerHTML`, AND NOT AS A MATTER OF TASTE. ts_headline wraps each match in
 * <b></b> and leaves the rest of the document's own text exactly as it found it -- it does not
 * escape anything. The text is written by people in the editor, so a document containing
 * `<img onerror=...>` would arrive here as markup and be run. Splitting on the two markers we put
 * there and rendering every other run as a React string is what makes the body text data again.
 */
export function parts(snippet: string | null | undefined): Part[] {
    const text = snippet ?? "";
    if (!text) return [];

    const out: Part[] = [];
    // Only the exact markers ts_headline is configured to emit. An unbalanced one -- a fragment cut
    // mid-match by MaxWords -- leaves its text in place rather than swallowing the rest of the line.
    const pattern = /<b>([\s\S]*?)<\/b>/g;
    let at = 0;
    let found: RegExpExecArray | null;
    while ((found = pattern.exec(text)) !== null) {
        if (found.index > at) out.push({ text: text.slice(at, found.index), hit: false });
        if (found[1]) out.push({ text: found[1], hit: true });
        at = found.index + found[0].length;
    }
    if (at < text.length) out.push({ text: text.slice(at), hit: false });
    return out;
}

/** The same text with the markers taken out -- for the places that show prose rather than a match. */
export function plain(snippet: string | null | undefined): string {
    return parts(snippet).map(p => p.text).join("");
}

/**
 * The same runs, with leading context dropped so the match itself is on screen.
 *
 * A ONE-LINE CELL TRUNCATES, AND IT TRUNCATES THE END. ts_headline centres its fragment on the
 * match, which is right for a paragraph and wrong for a row: "filled shape-changing modular
 * intraocular lens comprised of two co..." is a snippet whose whole purpose -- showing why this
 * document matched -- falls off the right edge. So when the first match starts late, the lead is
 * cut back to a few characters and an ellipsis says text was dropped.
 *
 * NOTHING IS DROPPED FROM THE MIDDLE OR THE END. What follows the match is the context that makes
 * it readable, and a reader who wants the rest opens the document.
 */
export function focused(snippet: string | null | undefined, lead = 24): Part[] {
    const runs = parts(snippet);
    const first = runs.findIndex(p => p.hit);
    if (first <= 0) return runs;

    const before = runs.slice(0, first).map(p => p.text).join("");
    if (before.length <= lead) return runs;

    // Back to a word boundary where there is one, so the cut does not land mid-word.
    const tail = before.slice(before.length - lead);
    const space = tail.indexOf(" ");
    return [{ text: "\u2026" + (space >= 0 ? tail.slice(space) : tail), hit: false },
            ...runs.slice(first)];
}

/**
 * Documents from THIS PERSONA'S OWN LIST whose number or name contains what was typed.
 *
 * A substring, case-insensitively, and not a ranked match: somebody typing `SOP-002` or `sterilis`
 * is naming a document they know exists, and the honest answer is the ones whose label contains
 * what they typed. Ranking belongs to the body index, which has the frequencies to do it with.
 */
export function byLabel(rows: DocumentRow[], query: string): DocumentRow[] {
    const text = query.trim().toLowerCase();
    if (!text) return [];
    return rows.filter(row =>
        (row.ObjectID ?? "").toLowerCase().includes(text) ||
        (row.Name ?? "").toLowerCase().includes(text));
}

/**
 * The two sets, as one list, in the order a reader expects.
 *
 * NAMES FIRST. Typing a document's number is naming that document; finding it third, under two
 * documents that merely mention it, reads as the search not working. Body hits keep the index's
 * own ranking after them, and a document in both appears once, as the name match it is -- carrying
 * its snippet, so the row still shows why the text matched too.
 */
export function merged(labelled: DocumentRow[], bodies: DocumentRow[]): DocumentRow[] {
    const snippets = new Map(bodies.map(row => [row.USID, row]));
    const seen = new Set<string>();
    const out: DocumentRow[] = [];

    for (const row of labelled) {
        if (seen.has(row.USID)) continue;
        seen.add(row.USID);
        const body = snippets.get(row.USID);
        out.push(body ? { ...row, Snippet: body.Snippet, Preview: body.Preview } : row);
    }
    for (const row of bodies) {
        if (seen.has(row.USID)) continue;
        seen.add(row.USID);
        out.push(row);
    }
    return out;
}
